import { useMemo } from 'react';
import styled from 'styled-components';
import { solfegeFor } from '../../../../../common/src/interval_training.ts';
import { Clef } from '../../../../../common/src/learn_rules.ts';
import { resultColorCss } from '../learn_colors.ts';
import { shortScreen } from '../learn_screen_parts.tsx';
import { shortScreenStaffWidth } from '../pitch/note_screen_parts.tsx';
import { PitchDotColor, pitchDotColorCss, pitchHintAmber, pitchLockedGreen } from '../pitch/pitch_colors.ts';
import { ledgerStepsFor, stemDirection } from '../pitch/staff_geometry.ts';
import { IntervalNoteDisplay } from './interval_session.ts';
import {
  intervalColumnInset,
  intervalColumnWidth,
  intervalColumnX,
  intervalDotStep,
  intervalDotX,
  intervalNoteStep,
  intervalStaffBand,
  intervalStaffHeight,
  intervalStaffLayout,
  intervalStepY,
} from './interval_staff_geometry.ts';
import { hearSyllableLabel } from './meet_syllables_screen.tsx';

// The staff Interval Training draws a pattern on (issue #3721), from the "Interval Training: listen",
// "sing it back" and "pattern result" artboards: Pitch Match's five lines with no clef, no letters
// and no captions, one column per note of the pattern, each note a notehead with a stem at the step
// its degree puts it on, and its syllable under it as a button that plays it; a tap on the note
// itself, anywhere in its column, plays it too (the product owner's ask), whenever the syllable
// would. Each note takes the colour of its state: grey still to come, the accent purple as it
// sounds and while it is being sung, then green, amber or red for how it went. The note sounding or
// being sung is drawn larger and its syllable bold. The singer's dot starts in the column of the note being sung and slides
// right toward the next note's column as the hold fills, arriving as the note locks (a trial the
// product owner asked for: the dot reaching the next note says it is time to sing it); on the
// result, a note that ended missed or close shows where the singer landed, in its own colour.
//
// Every number comes from interval_staff_geometry.ts, so this file only turns geometry into SVG; it
// holds no state and knows nothing about audio. The staff is as tall as the session's band, which
// the screen works out from every pattern of the session, so it does not change between patterns.

export const intervalStaffTestId = 'interval-staff';
export const intervalNoteTestId = 'interval-note';
export const intervalNoteHitTestId = 'interval-note-hit';
export const intervalLedgerTestId = 'interval-ledger';
export const intervalDotTestId = 'interval-dot';
export const intervalLandedTestId = 'interval-landed';
export const intervalSyllableTestId = 'interval-syllable';

const staffLineColor = 'var(--color-text)';
// The page behind the staff: the dot's ring is cut out of it, so it reads over a staff line
const paperColor = 'var(--color-background)';
const accentPurple = 'var(--color-tab-selected)';

// A note's colour on the staff, by its state. Grey is the tab bar's unselected grey, the same in
// both themes, so a note still to come recedes on either background.
export const intervalNoteColors: Record<IntervalNoteDisplay, string> = {
  upcoming: 'var(--color-tab-label-unselected)',
  sounding: accentPurple,
  sounded: accentPurple,
  current: accentPurple,
  onPitch: pitchLockedGreen,
  close: resultColorCss.yellow,
  missed: resultColorCss.red,
};

// A syllable's colour: the note's, but text, so the grey is the page's label grey and the close
// yellow is the darker amber Pitch Match sets its hints in, which reads as type
export const intervalSyllableColors: Record<IntervalNoteDisplay, string> = {
  ...intervalNoteColors,
  upcoming: 'var(--color-text-darker)',
  close: pitchHintAmber,
};

// The note sounding (in listen) or being sung is drawn larger with its syllable bold, and so is a
// note sounding now in any phase - replayed while singing, or its syllable tapped on the result -
// which keeps its own colour and landing: the colour is how it went, the emphasis that it is in the
// air
function isEmphasized(note: IntervalStaffNote): boolean {
  return note.sounding || note.display === 'sounding' || note.display === 'current';
}

// One note of the pattern on screen: its state, whether it is sounding now, and on the result
// screen only, where the singer landed on a note that ended missed or close
export interface IntervalStaffNote {
  degree: number;
  display: IntervalNoteDisplay;
  sounding: boolean;
  landedCents?: number;
}

// The singer, from the session's intervalDot, on note `index`: its height is the cents against that
// note, and `held`, the hold's fraction (0 to 1, from intervalHoldProgress), slides it right from the
// note's column toward the next one (intervalDotX)
export interface IntervalStaffDot {
  index: number;
  cents: number;
  color: PitchDotColor;
  held: number;
}

export const IntervalStaff = ({
  clef,
  homeMidi,
  lowDegree,
  highDegree,
  notes,
  dot,
  syllablesPlay,
  onTapSyllable,
}: {
  clef: Clef; // the singer's, from clefForVoicePart
  homeMidi: number;
  lowDegree: number; // the lowest and highest degree any pattern of the session sings: the band
  highDegree: number;
  notes: IntervalStaffNote[];
  dot?: IntervalStaffDot;
  // A tap on a syllable would play its note now (the session's canPlayPatternNote); the buttons are
  // disabled when it would not, keeping their colours, which are the notes' states. A tap on a note
  // on the staff follows the same rule and calls the same callback as its syllable.
  syllablesPlay: boolean;
  onTapSyllable: (index: number) => void;
}) => {
  const layout = intervalStaffLayout;
  // The band is the session's, so it changes only with these; the staff renders on every mic frame
  const band = useMemo(
    () => intervalStaffBand({ homeMidi, clef, lowDegree, highDegree }),
    [homeMidi, clef, lowDegree, highDegree],
  );
  const height = intervalStaffHeight(band);
  // One column per note: its place in the pattern is its identity, since a pattern can sing a
  // syllable twice
  const columns = notes.map((note, index) => ({
    ...note,
    index,
    step: intervalNoteStep(homeMidi, note.degree, clef),
    x: intervalColumnX(index, notes.length),
  }));
  const dotColumn = dot && columns[dot.index];
  const columnWidth = intervalColumnWidth(notes.length);
  return (
    <StaffBox>
      <Svg
        data-testid={intervalStaffTestId}
        data-clef={clef}
        data-min-step={band.minStep}
        data-max-step={band.maxStep}
        viewBox={`0 0 ${layout.width} ${height}`}
      >
        {[0, 2, 4, 6, 8].map((step) => (
          <line
            key={step}
            x1={layout.lineX1}
            y1={intervalStepY(step, band)}
            x2={layout.lineX2}
            y2={intervalStepY(step, band)}
            stroke={staffLineColor}
            strokeWidth={layout.lineWidth}
          />
        ))}
        {columns.map((note) => {
          const { index, step, x } = note;
          const y = intervalStepY(step, band);
          const color = intervalNoteColors[note.display];
          const head = isEmphasized(note) ? layout.emphasizedNotehead : layout.normalNotehead;
          const stem = stemDirection(step);
          const stemOffset = head.rx - layout.stemInsetX;
          const stemX = x + (stem === 'up' ? stemOffset : -stemOffset);
          const sign = stem === 'up' ? -1 : 1;
          return (
            <NoteGroup
              key={index}
              data-testid={intervalNoteTestId}
              data-index={index}
              data-degree={note.degree}
              data-step={step}
              data-stem={stem}
              data-display={note.display}
              data-sounding={note.sounding ? 'true' : 'false'}
              data-playable={syllablesPlay ? 'true' : 'false'}
              onClick={syllablesPlay ? () => onTapSyllable(index) : undefined}
            >
              {/* The note's tap area: its whole column, the staff's full height, under the notehead,
                  stem and ledger lines, which take a tap too. Painted transparent, so it draws
                  nothing and still takes pointer events. Hidden from assistive technology: the
                  syllable under the note is the one control that plays it. */}
              <rect
                data-testid={intervalNoteHitTestId}
                data-index={index}
                aria-hidden="true"
                x={x - columnWidth / 2}
                y={0}
                width={columnWidth}
                height={height}
                fill="transparent"
              />
              {/* Each ledger line is a short line centred on its own notehead, as printed music draws it */}
              {ledgerStepsFor(step).map((ledger) => (
                <line
                  key={ledger}
                  data-testid={intervalLedgerTestId}
                  data-index={index}
                  data-step={ledger}
                  x1={x - layout.ledgerHalfWidth}
                  y1={intervalStepY(ledger, band)}
                  x2={x + layout.ledgerHalfWidth}
                  y2={intervalStepY(ledger, band)}
                  stroke={staffLineColor}
                  strokeWidth={layout.lineWidth}
                />
              ))}
              <ellipse
                cx={x}
                cy={y}
                rx={head.rx}
                ry={head.ry}
                transform={`rotate(${layout.noteheadRotation} ${x} ${y})`}
                fill={color}
              />
              <line
                x1={stemX}
                y1={y + sign * layout.stemFootOffsetY}
                x2={stemX}
                y2={y + sign * layout.stemLength}
                stroke={color}
                strokeWidth={layout.stemWidth}
              />
            </NoteGroup>
          );
        })}
        {columns.map((note) => {
          if (note.landedCents === undefined || (note.display !== 'missed' && note.display !== 'close')) {
            return null;
          }
          const y = intervalStepY(intervalDotStep(note.step, note.landedCents, band), band);
          return (
            <Marker
              key={note.index}
              testId={intervalLandedTestId}
              index={note.index}
              x={note.x}
              y={y}
              color={intervalNoteColors[note.display]}
              data={{ 'data-result': note.display, 'data-cents': Math.round(note.landedCents) }}
            />
          );
        })}
        {dot && dotColumn && (
          <Marker
            testId={intervalDotTestId}
            index={dot.index}
            x={intervalDotX(dot.index, notes.length, dot.held)}
            y={intervalStepY(intervalDotStep(dotColumn.step, dot.cents, band), band)}
            color={pitchDotColorCss[dot.color]}
            data={{ 'data-color': dot.color, 'data-cents': Math.round(dot.cents) }}
          />
        )}
      </Svg>
      <Syllables>
        {columns.map((note) => {
          const syllable = solfegeFor(note.degree);
          return (
            <SyllableButton
              key={note.index}
              type="button"
              data-testid={intervalSyllableTestId}
              data-index={note.index}
              data-display={note.display}
              data-sounding={note.sounding ? 'true' : 'false'}
              aria-label={hearSyllableLabel(syllable)}
              disabled={!syllablesPlay}
              $emphasized={isEmphasized(note)}
              style={{ color: intervalSyllableColors[note.display] }}
              onClick={() => onTapSyllable(note.index)}
            >
              {syllable}
            </SyllableButton>
          );
        })}
      </Syllables>
    </StaffBox>
  );
};

// The dot and a landing share one look, the artboards' filled circle on a ring of paper with a
// halo. Every frame moves the dot to the step the cents put it on and across as far as the hold has
// filled, with no CSS transition: the state machine smooths the reading, and a transition
// retargeted each frame never completes in WebKit - the dot sat at the singer's last pause while
// the words were live (issue #3587, pitch_staff.tsx). Where it is across is in the transform, which
// is what tests read; there is no attribute of its own for it on every frame. A tap
// passes through it to the note under it: the dot sits over the note being sung, and a landing over
// the note it belongs to.
const Marker = ({
  testId,
  index,
  x,
  y,
  color,
  data,
}: {
  testId: string;
  index: number;
  x: number;
  y: number;
  color: string;
  data: Record<string, string | number>;
}) => {
  const layout = intervalStaffLayout;
  return (
    <g
      data-testid={testId}
      data-index={index}
      {...data}
      style={{ transform: `translate(${x}px, ${y}px)`, pointerEvents: 'none' }}
    >
      <circle cx={0} cy={0} r={layout.dotRadius} fill={color} stroke={paperColor} strokeWidth={layout.dotStrokeWidth} />
      <circle
        cx={0}
        cy={0}
        r={layout.haloRadius}
        fill="none"
        stroke={color}
        strokeWidth={layout.haloStrokeWidth}
        opacity={layout.haloOpacity}
      />
    </g>
  );
};

// The staff is drawn at the artboards' 350 units and scaled to whatever the screen gives it, as
// Pitch Match's is; the syllables below it scale with it, so each stays under its note
const StaffBox = styled.div`
  width: 100%;
  max-width: ${intervalStaffLayout.width}px;
  margin: 0 auto;

  ${shortScreen} {
    max-width: ${shortScreenStaffWidth}px;
  }
`;

const Svg = styled.svg`
  display: block;
  width: 100%;
  height: auto;
`;

// A note takes a tap exactly when its syllable does, and dims while pressed as its syllable does
const NoteGroup = styled.g`
  -webkit-tap-highlight-color: transparent;

  &[data-playable='true'] {
    cursor: pointer;
  }

  &[data-playable='true']:active {
    opacity: 0.6;
  }
`;

// Padded by the staff lines' inset, so n equal buttons put each syllable under its note's column.
// Drawn up into the staff's bottom padding, which is only there for a dot clamped to the band's
// foot, so the syllables sit as close under the notes as the artboards put them.
const Syllables = styled.div`
  display: flex;
  margin-top: -12px;
  padding: 0 ${intervalColumnInset * 100}%;
`;

const SyllableButton = styled.button<{ $emphasized: boolean }>`
  flex: 1 1 0;
  min-width: 0;
  min-height: 44px;
  padding: 0;
  border: none;
  background: none;
  font-family: ${({ $emphasized }) =>
    $emphasized ? 'Jost-SemiBold, Arial, sans-serif' : 'Jost-Regular, Arial, sans-serif'};
  font-size: ${({ $emphasized }) => ($emphasized ? 17 : 15)}px;
  line-height: 1;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &:active:not(:disabled) {
    opacity: 0.6;
  }

  &:disabled {
    cursor: default;
  }
`;
